import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { CircleNotch, FloppyDisk, X } from "@phosphor-icons/react";
import { SessionScheduleBuilder, type SessionEntry } from "./SessionScheduleBuilder";
import { ClientPriceCalculator } from "@/components/pricing/ClientPriceCalculator";
import { usePricingConfig } from "@/hooks/usePricingConfig";

export interface GroupTherapyFormData {
  name: string;
  description: string;
  price: number | null;
  maxParticipants: number;
  durationMinutes: number;
  sessions: SessionEntry[];
}

export const emptyGroupTherapyForm: GroupTherapyFormData = {
  name: "",
  description: "",
  price: null,
  maxParticipants: 8,
  durationMinutes: 90,
  sessions: [],
};

interface Props {
  form: GroupTherapyFormData;
  setForm: React.Dispatch<React.SetStateAction<GroupTherapyFormData>>;
  editingId: string | null;
  isSaving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

export function GroupTherapyForm({ form, setForm, editingId, isSaving, onSubmit, onCancel }: Props) {
  const pricingConfig = usePricingConfig();
  const canSubmit =
    !!form.name.trim() &&
    !!form.description.trim() &&
    !!form.price &&
    form.maxParticipants >= 2 &&
    form.durationMinutes >= 15 &&
    form.sessions.length >= 1 &&
    form.sessions.every((s) => s.date && s.time);

  return (
    <Card className="border-[#ece8df]">
      <CardHeader>
        <CardTitle style={{ fontFamily: "var(--font-heading)" }}>
          {editingId ? "Uredi grupnu terapiju" : "Nova grupna terapija"}
        </CardTitle>
        <CardDescription>
          Definišite naziv, cijenu, broj učesnika i raspored termina. Ciklus se zaključava
          kad prva sesija počne — nakon toga nove prijave nisu moguće.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="gt-name">Naziv</Label>
          <Input
            id="gt-name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="npr. Grupa za suočavanje sa anksioznošću"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="gt-desc">Opis</Label>
          <Textarea
            id="gt-desc"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            className="min-h-28"
            placeholder="Za koga je grupna terapija, šta će se raditi, koje je trajanje ciklusa..."
          />
        </div>

        <div className="grid gap-6 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="gt-price">Cijena za klijenta (cijeli ciklus, sa PDV-om)</Label>
            <CurrencyInput
              id="gt-price"
              value={form.price}
              onChange={(v) => setForm((p) => ({ ...p, price: v }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt-max">Maks. učesnika</Label>
            <Input
              id="gt-max"
              type="number"
              min={2}
              max={100}
              value={form.maxParticipants}
              onChange={(e) =>
                setForm((p) => ({ ...p, maxParticipants: parseInt(e.target.value) || 2 }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gt-dur">Trajanje sesije (min)</Label>
            <Input
              id="gt-dur"
              type="number"
              min={15}
              max={480}
              value={form.durationMinutes}
              onChange={(e) =>
                setForm((p) => ({ ...p, durationMinutes: parseInt(e.target.value) || 60 }))
              }
            />
          </div>
        </div>

        <ClientPriceCalculator amount={form.price} pricing={pricingConfig} direction="grossToNet" />

        <div className="space-y-2">
          <Label>Termini</Label>
          <SessionScheduleBuilder
            sessions={form.sessions}
            onChange={(sessions) => setForm((p) => ({ ...p, sessions }))}
          />
        </div>
      </CardContent>
      <CardFooter className="gap-3">
        <Button onClick={onSubmit} disabled={isSaving || !canSubmit}>
          {isSaving ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
          {editingId ? "Spremi izmjene" : "Kreiraj grupnu terapiju"}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          <X className="size-4" /> Otkaži
        </Button>
      </CardFooter>
    </Card>
  );
}
